import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Terms: React.FC = () => {
    const { hash } = useLocation();

    useEffect(() => {
      if (hash) {
        document.getElementById(hash.substring(1))?.scrollIntoView();
      }
    }, [hash]);

    return (
        <section className="hero-section terms-section text-center text-white">
          <h1 className="hero-headline">Website Terms of Use</h1>
          <div className="hero-paragraph text-start mx-auto" style={{ maxWidth: '760px' }}>
            <p className="legal-effective">Version 1.0 — effective 12 September 2026</p>
            <p>
              This website, voycalc.com, is operated by <strong>VoyCalc AS</strong>, org. no. 938 332 959,
              Lindbackveien 9D, 1163 Oslo, Norway ("VoyCalc", "we", "us").
              Contact: <a href="mailto:legal@voycalc.com" className="text-white">legal@voycalc.com</a>.
            </p>
            <p>
              By accessing or using this website, you agree to these Terms of Use. If you do not agree,
              please do not use the site.
            </p>

            <h2 className="mt-5 h4">1. Scope</h2>
            <p>1.1 These Terms of Use govern your use of this website and any content, demonstration or trial access made available through it.</p>
            <p>
              1.2 <strong>They do not govern the VoyCalc service itself.</strong> Access to the VoyCalc platform
              as a subscriber is governed by the VoyCalc Terms of Service, agreed separately with the subscribing
              customer. Where the two conflict in relation to the platform, the Terms of Service prevail.
            </p>
            <p>1.3 The VoyCalc service is offered to businesses only. This website is not directed at consumers, and nothing on it constitutes an offer capable of acceptance.</p>

            <h2 className="mt-5 h4">2. Use of the website</h2>
            <p>2.1 You may view, download and print pages from this site for your own business purposes.</p>
            <p>2.2 You may not: use the site in any unlawful way; attempt to gain unauthorised access to any part of it or to any server or system connected to it; introduce malicious code; scrape, harvest or systematically extract content; or use the site in a way that impairs its availability for others.</p>
            <p>2.3 We may withdraw or restrict access to all or part of the site at any time and without notice.</p>

            <h2 className="mt-5 h4">3. Intellectual property</h2>
            <p>3.1 All content on this site, including text, graphics, layout, software, screenshots and the VoyCalc name and logo, is owned by VoyCalc AS or its licensors and is protected by copyright and other intellectual property rights.</p>
            <p>3.2 You may not reproduce, adapt, distribute or publish any part of it commercially without our prior written consent.</p>
            <p>3.3 Where the site displays information from the E.U. Copernicus Marine Service or other third-party data providers, that information remains subject to the terms of the relevant provider, and any attribution or Digital Object Identifier displayed with it must be preserved.</p>

            <h2 className="mt-5 h4">4. No advice, and no reliance on figures</h2>
            <p>4.1 Information on this site is provided for general information only. It does not constitute commercial, technical, financial or legal advice, and must not be relied on as such.</p>
            <p>
              4.2 Any calculation, estimate, distance, weather factor, bunker figure, screenshot or sample result
              shown on this site is <strong>illustrative only</strong>. It is not verified, is not tailored to any
              specific voyage or vessel, and must not be used as a basis for any operational, financial or
              commercial decision.
            </p>
            <p>4.3 We give no warranty that content on this site is accurate, complete or current, and we may change it at any time without notice.</p>

            <h2 className="mt-5 h4">5. Demonstrations and trial access</h2>
            <p>5.1 Where we make a demonstration, sandbox or trial version of the VoyCalc platform available through this site, it is provided <strong>free of charge, "as is", and for evaluation only</strong>.</p>
            <p>5.2 Output from a demonstration or trial carries no warranty of any kind and must not be relied on in any live commercial context.</p>
            <p>5.3 We may modify, suspend or withdraw demonstration or trial access at any time, without notice and without liability.</p>

            <h2 className="mt-5 h4">6. Links to other sites</h2>
            <p>Links to third-party websites are provided for convenience. We do not control them, do not endorse their content, and accept no responsibility for them or for any loss arising from your use of them.</p>

            <h2 className="mt-5 h4">7. Privacy and cookies</h2>
            <p>
              7.1 Personal data collected through this site is handled as described in our{' '}
              <a href="/?page=privacy" className="text-white">Privacy Policy</a>.
            </p>
            <p>
              7.2 Information stored on your device in connection with this site is described in our{' '}
              <a href="/?page=cookies" className="text-white">Cookie Policy</a>.
            </p>

            <h2 className="mt-5 h4">8. Availability</h2>
            <p>We do not warrant that this site will be available uninterrupted or free of errors, and we accept no liability for any period of unavailability.</p>

            <h2 className="mt-5 h4">9. Limitation of liability</h2>
            <p>9.1 Nothing in these Terms of Use limits or excludes liability for gross negligence or wilful misconduct, for death or personal injury caused by negligence, or for any other liability that cannot lawfully be limited.</p>
            <p>9.2 Subject to clause 9.1, and to the fullest extent permitted by law, VoyCalc AS shall not be liable for any loss of profit, loss of revenue, loss of contract or fixture, business interruption, loss or corruption of data, or any indirect or consequential loss arising from your use of, or inability to use, this website or any content on it.</p>
            <p>9.3 If any limitation in this clause is held unenforceable in whole or in part, it shall apply to the maximum extent permitted by law, and the remaining limitations shall continue in full force.</p>

            <h2 className="mt-5 h4">10. Changes to these terms</h2>
            <p>We may revise these Terms of Use at any time. The version published on the site at the time of your visit is the version that applies. The effective date is shown at the top.</p>

            <h2 className="mt-5 h4">11. Governing law and jurisdiction</h2>
            <p>11.1 These Terms of Use, and any dispute arising out of or in connection with them, are governed by <strong>the laws of Norway</strong>.</p>
            <p>11.2 The <strong>Oslo District Court</strong> is the agreed venue for any dispute, subject to any mandatory rule of law that provides otherwise.</p>
            <p>11.3 Disputes concerning the VoyCalc platform between VoyCalc AS and a subscribing customer are resolved under the dispute resolution clause of the Terms of Service, not this clause.</p>

            <h2 className="mt-5 h4" id="data-sources">Data sources &amp; attributions</h2>
            <p>
              VoyCalc's Weather Factor and Weather in Port statistics are derived from Copernicus data:
            </p>
            <ul>
              <li>
                Contains modified Copernicus Climate Change Service information (ERA5 reanalysis, accessed via the
                Copernicus Climate Data Store and the ARCO-ERA5 mirror hosted on Google Cloud Public Datasets), 2026.
                Neither the European Commission nor ECMWF is responsible for any use that may be made of the Copernicus
                information or data it contains.
              </li>
              <li>
                Generated using E.U. Copernicus Marine Service Information (GLORYS12V1 global ocean reanalysis);{' '}
                <a href="https://doi.org/10.48670/moi-00021" className="text-white" target="_blank" rel="noreferrer">
                  https://doi.org/10.48670/moi-00021
                </a>.
              </li>
              <li>
                ENSO indices courtesy of the NOAA Physical Sciences Laboratory and the IRI/CPC ENSO forecast
                (Columbia University). Land mask derived from Natural Earth.
              </li>
            </ul>

            <p className="legal-company mt-5">
              VoyCalc AS — org. no. 938 332 959 — Lindbackveien 9D, 1163 Oslo, Norway
            </p>
          </div>
        </section>
    );
}

export default Terms
